import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import * as THREE from 'three';

// UUID generator for session_id (same helper used in ExplanationScreen)
function newUUID() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SUBJECT_API_KEY = {
  'اللغة العربية': 'arabic_language',
};

const SUBJECT_META = {
  'Mathematics':              { icon: '🔢' },
  'Science':                  { icon: '🔬' },
  'English':                  { icon: '📖' },
  'Computing':                { icon: '💻' },
  'Global Perspectives':      { icon: '🌍' },
  'Modern Foreign Language':  { icon: '🗣️' },
  'التربية الإسلامية':        { icon: '☪️' },
  'اللغة العربية':            { icon: '✍️' },
};
const DEFAULT_META = { icon: '📚' };

// Match the same list used in ExplanationScreen.jsx for consistency
const ARABIC_ONLY_SUBJECTS = [
  'التربية الإسلامية',
  'اللغة العربية',
  'تاريخ قطر والمواطنة',
];

// Bilingual labels — chosen per subject, NOT per UI language
const LABELS = {
  ar: {
    loading:              'جارٍ التحميل…',
    retry:                'إعادة المحاولة',
    subject_not_found:    'لم يتم العثور على المادة',
    load_failed:          'تعذّر تحميل المادة',
    start:                'ابدأ ←',
    review:               'مراجعة ←',
    start_with_count:     (n) => `ابدأ (${n}) ←`,
    select_at_least_one:  'اختر موضوعًا واحدًا على الأقل',
    topics_selected:      (n) => n === 1 ? 'موضوع واحد محدد' :
                                 n === 2 ? 'موضوعان محددان' :
                                 `${n} مواضيع محددة`,
    term_one:             '📚 الفصل الأول',
    term_two:             '📚 الفصل الثاني',
    hifz_test:            'اختبار الحفظ',
    hifz_loading:         'جاري التحضير...',
  },
  en: {
    loading:              'Loading…',
    retry:                'Try Again',
    subject_not_found:    'Subject not found',
    load_failed:          'Failed to load subject',
    start:                'Start →',
    review:               'Review →',
    start_with_count:     (n) => `Start (${n}) →`,
    select_at_least_one:  'Select at least one topic',
    topics_selected:      (n) => n === 1 ? '1 topic selected' :
                                 `${n} topics selected`,
    term_one:             '📚 Term 1',
    term_two:             '📚 Term 2',
    hifz_test:            'Memorization Test',
    hifz_loading:         'Preparing...',
  },
};

const ARABIC_ORDINALS = [
  'الأول','الثاني','الثالث','الرابع','الخامس',
  'السادس','السابع','الثامن','التاسع','العاشر',
  'الحادي عشر','الثاني عشر','الثالث عشر','الرابع عشر','الخامس عشر',
];

// Subjects whose unit.topics contain content categories (e.g. "أهداف الدرس",
// "الأنشطة والتدريبات") rather than student-selectable concepts. The backend
// handles categorization internally via its qatar_history classification logic.
const HIDE_TOPICS_SUBJECTS = new Set([
  'تاريخ قطر والمواطنة',
]);

// Scaffolding section names that appear in topic_list but are not selectable learning topics.
// These are structural chunks (navigation, review prompts) kept in DB for RAG but hidden from the picker.
const SCAFFOLDING_SECTIONS = new Set([
  'getting started',
  'unit introduction',
  'check your progress',
  'introduction',
  'project',
  'glossary',
]);

export default function SubjectUnitsPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const navState  = location.state || {};
  const { lang }  = useLanguage();

  const studentRaw = localStorage.getItem('student_data');
  const student    = studentRaw ? JSON.parse(studentRaw) : null;
  const token      = localStorage.getItem('student_token') || '';

  const [subj,            setSubj]            = useState(navState.subj || null);
  const [loading,         setLoading]         = useState(!navState.subj);
  const [error,           setError]           = useState(null);
  const [completedTopics, setCompletedTopics] = useState([]);

  // Subject language is determined by the subject itself, not the UI lang.
  // Falls back to `lang` while subject is loading.
  const isArabicSubject = subj
    ? ARABIC_ONLY_SUBJECTS.includes(subj.subject_name)
    : lang === 'ar';
  const contentLang = isArabicSubject ? 'ar' : 'en';
  const L = LABELS[contentLang];
  const contentDir = isArabicSubject ? 'rtl' : 'ltr';

  // Per-unit multi-topic selection — shape: { [unit_id]: Set<topic_name> }
  const [selectedTopicsByUnit, setSelectedTopicsByUnit] = useState({});
  const [memLoadingUnit,       setMemLoadingUnit]       = useState(null);

  const toggleTopic = (unit_id, topic_name) => {
    setSelectedTopicsByUnit(prev => {
      const current = new Set(prev[unit_id] || []);
      if (current.has(topic_name)) current.delete(topic_name);
      else current.add(topic_name);
      return { ...prev, [unit_id]: current };
    });
  };

  const getSelectedCount = (unit_id) =>
    (selectedTopicsByUnit[unit_id]?.size || 0);

  const getSelectedArray = (unit_id) =>
    Array.from(selectedTopicsByUnit[unit_id] || []);

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  /* If only subject_id was passed, fetch subjects and find the right one */
  const loadSubject = () => {
    if (!student || !token) { navigate('/student/auth', { replace: true }); return; }

    const subject_id = navState.subject_id;
    if (!subject_id) { navigate('/student/subjects', { replace: true }); return; }

    setLoading(true);
    setError(null);
    studentFetch(`${API}/student/subjects/${student.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          const found = (data.subjects || []).find(s => s.subject_id === subject_id);
          if (found) setSubj(found);
          else setError(L.subject_not_found);
        } else {
          setError(L.load_failed);
        }
      })
      .catch(() => setError(L.load_failed))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (subj) return;
    loadSubject();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!subj || !student || !token) return;
    const allUnitIds = (subj.units || []).map(u => u.unit_id).filter(Boolean);
    Promise.all(
      allUnitIds.map(uid =>
        studentFetch(`${API}/student/completed-topics/${student.id}/${uid}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r ? r.json() : { completed_topics: [] }).catch(() => ({ completed_topics: [] }))
      )
    ).then(results => {
      const allCompleted = results.flatMap(r => r.completed_topics || []);
      setCompletedTopics(allCompleted);
    });
  }, [subj]); // eslint-disable-line react-hooks/exhaustive-deps

  function goToUnit(unit, overrideSelectedTopics = null) {
    // Mirror UnitCard's logic: subjects in HIDE_TOPICS_SUBJECTS are
    // treated as direct-start lessons regardless of raw unit.topics.
    const hideTopicsForSubject = HIDE_TOPICS_SUBJECTS.has(subj?.subject_name);
    const hasTopics = !hideTopicsForSubject && (unit.topics || []).length > 0;
    let selected = overrideSelectedTopics;

    if (!selected) {
      selected = hasTopics ? getSelectedArray(unit.unit_id) : [];
    }

    // For units with topics, require at least one selection
    if (hasTopics && selected.length === 0) return;

    // Generate ONE session_id that will be shared across all selected topics
    const session_id = newUUID();

    navigate('/student/explanation', {
      state: {
        student_id:    student.id,
        subject_id:    subj.subject_id,
        subject:       subj.subject_name,
        unit_id:       unit.unit_id,
        unit:          unit.unit_name,
        // Legacy single-topic fields preserved for ExplanationScreen's
        // fallback path (when selected_topics is empty):
        topic:         hasTopics ? (selected[0] || '') : unit.unit_name,
        topic_list:    (unit.topics || []).map(t => t.topic_name),
        // NEW: multi-topic selection
        selected_topics: hasTopics ? selected : [unit.unit_name],
        // Shared session id for the whole session
        session_id,
        grade:         student.grade || parseInt(
                         localStorage.getItem('jahbath_student_grade') || '0', 10
                       ),
        is_custom:     false,
        // Poem display fields — used by ExplanationScreen to show full poem panel
        poem_title:    unit?.poem_title    || null,
        has_poem:      unit?.has_poem      || false,
      },
    });
  }

  async function goToMemorizationQuiz(unit) {
    if (!unit?.poem_title) return;
    setMemLoadingUnit(unit.unit_id);
    try {
      const sessionId = newUUID();
      const res = await studentFetch(`${API}/session/memorization-quiz`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('student_token') || ''}`,
        },
        body: JSON.stringify({
          student_id: student.id,
          session_id: sessionId,
          grade:      student.grade,
          subject:    SUBJECT_API_KEY[subj.subject_name] ?? subj.subject_name,
          unit_name:  (unit.unit_name || '').split(':')[0].trim(),
          poem_title: unit.poem_title,
        }),
      });
      const data = await res.json();
      if (!data?.success || !Array.isArray(data?.questions) || data.questions.length === 0) {
        alert('تعذر تحضير اختبار الحفظ. حاول مرة أخرى.');
        setMemLoadingUnit(null);
        return;
      }
      navigate('/student/quiz', {
        state: {
          student_id:        student.id,
          session_id:        sessionId,
          subject:           subj.subject_name,
          unit:              unit.unit_name,
          grade:             student.grade,
          exam_type:         'memorization',
          questions:         data.questions,
          delivery_language: lang,
          subject_id:        subj.subject_id ?? null,
          unit_id:           unit.unit_id,
          return_to:         window.location.pathname,
          resume_navstate:   {},
        },
      });
    } catch (err) {
      console.error('Memorization quiz launch failed:', err);
      alert('حدث خطأ. حاول مرة أخرى.');
      setMemLoadingUnit(null);
    }
  }

  const allUnits = subj?.units || [];

  // Semester split only applies to Arabic-only subjects with semester data
  const hasSemesters = isArabicSubject &&
                       allUnits.some(u => u.semester != null);
  const unitsSem1    = hasSemesters ? allUnits.filter(u => u.semester === 1) : allUnits;
  const unitsSem2    = hasSemesters ? allUnits.filter(u => u.semester === 2) : [];

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: "'Sora',sans-serif", direction: contentDir }}>
      <canvas ref={canvasRef} style={{ position:'fixed', inset:0, zIndex:0, pointerEvents:'none' }} />
      <style>{CSS}</style>

      <div style={{ position: 'relative', zIndex: 10 }}>
        <StudentHeader
          title={subj ? subj.subject_name : ''}
          showBack
          onBack={() => navigate('/student/subjects')}
          lang={lang}
        />

        <main style={{ maxWidth: 860, margin: '0 auto', padding: '32px 24px 80px' }}>

        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 20px', gap: 16 }}>
            <div className="sup-spinner" />
            <p style={{ color: '#6B7280', fontSize: 15 }}>{L.loading}</p>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: 'center', padding: '80px 20px' }}>
            <div style={{ fontSize: '2rem', marginBottom: 12 }}>⚠️</div>
            <div style={{ color: '#DC2626', fontSize: 15, marginBottom: 16 }}>{error}</div>
            <button
              onClick={loadSubject}
              style={{
                background: 'linear-gradient(135deg,#059669,#10B981)',
                color: 'white', border: 'none', borderRadius: 10,
                padding: '10px 24px', fontSize: 14, cursor: 'pointer',
                fontFamily: 'inherit',
              }}>
              {L.retry}
            </button>
          </div>
        )}

        {!loading && !error && subj && (
          <>
            {hasSemesters ? (
              <>
                <SemesterSection
                  title={L.term_one}
                  units={unitsSem1}
                  subjectName={subj.subject_name}
                  goToUnit={goToUnit}
                  completedTopics={completedTopics}
                  selectedTopicsByUnit={selectedTopicsByUnit}
                  toggleTopic={toggleTopic}
                  getSelectedCount={getSelectedCount}
                  L={L}
                  contentDir={contentDir}
                  onMemorizationClick={goToMemorizationQuiz}
                  memLoadingUnit={memLoadingUnit}
                />
                {unitsSem2.length > 0 && (
                  <>
                    <div style={{ borderTop: '2px solid #E5E7EB', margin: '32px 0' }} />
                    <SemesterSection
                      title={L.term_two}
                      units={unitsSem2}
                      subjectName={subj.subject_name}
                      goToUnit={goToUnit}
                      completedTopics={completedTopics}
                      selectedTopicsByUnit={selectedTopicsByUnit}
                      toggleTopic={toggleTopic}
                      getSelectedCount={getSelectedCount}
                      L={L}
                      contentDir={contentDir}
                      onMemorizationClick={goToMemorizationQuiz}
                      memLoadingUnit={memLoadingUnit}
                    />
                  </>
                )}
              </>
            ) : (
              <UnitsGrid
                units={allUnits}
                subjectName={subj.subject_name}
                goToUnit={goToUnit}
                completedTopics={completedTopics}
                selectedTopicsByUnit={selectedTopicsByUnit}
                toggleTopic={toggleTopic}
                getSelectedCount={getSelectedCount}
                L={L}
                contentDir={contentDir}
                onMemorizationClick={goToMemorizationQuiz}
                memLoadingUnit={memLoadingUnit}
              />
            )}
          </>
        )}
        </main>
      </div>
    </div>
  );
}

function SemesterSection({ title, units, subjectName, goToUnit, completedTopics,
                            selectedTopicsByUnit,
                            toggleTopic, getSelectedCount, L, contentDir,
                            onMemorizationClick, memLoadingUnit }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <h2 style={{
        fontSize: 18, fontWeight: 700, color: '#1E3A5F',
        marginBottom: 16, fontFamily: "'Sora',sans-serif"
      }}>
        {title}
      </h2>
      <UnitsGrid
        units={units}
        subjectName={subjectName}
        goToUnit={goToUnit}
        completedTopics={completedTopics}
        selectedTopicsByUnit={selectedTopicsByUnit}
        toggleTopic={toggleTopic}
        getSelectedCount={getSelectedCount}
        L={L}
        contentDir={contentDir}
        onMemorizationClick={onMemorizationClick}
        memLoadingUnit={memLoadingUnit}
      />
    </div>
  );
}

function UnitsGrid({ units, subjectName, goToUnit, completedTopics,
                     selectedTopicsByUnit, toggleTopic, getSelectedCount,
                     L, contentDir, onMemorizationClick, memLoadingUnit }) {
  return (
    <div style={{ display: 'grid', width: '100%', gap: 20, direction: contentDir }}
         className="sup-grid">
      {units.map((unit, idx) => (
        <UnitCard
          key={unit.unit_id || idx}
          unit={unit}
          subjectName={subjectName}
          globalIndex={idx}
          goToUnit={goToUnit}
          completedTopics={completedTopics}
          selected={selectedTopicsByUnit[unit.unit_id]}
          toggleTopic={toggleTopic}
          selectedCount={getSelectedCount(unit.unit_id)}
          L={L}
          contentDir={contentDir}
          onMemorizationClick={onMemorizationClick}
          memLoadingUnit={memLoadingUnit}
        />
      ))}
    </div>
  );
}

function UnitCard({ unit, subjectName, globalIndex, goToUnit, completedTopics,
                   selected, toggleTopic, selectedCount,
                   L, contentDir, onMemorizationClick, memLoadingUnit }) {
  const hideTopicsForSubject = HIDE_TOPICS_SUBJECTS.has(subjectName);
  const rawTopics  = unit.topics || [];
  const topics     = hideTopicsForSubject
    ? []
    : rawTopics.filter(tp => !SCAFFOLDING_SECTIONS.has((tp.topic_name || '').toLowerCase()));
  const hasTopics  = topics.length > 0;
  const canStart   = hasTopics ? selectedCount > 0 : true;
  const allTopicsCompleted = hasTopics &&
    topics.every(t => completedTopics.includes(t.topic_name));

  const startLabel = hasTopics
    ? (selectedCount > 0 ? L.start_with_count(selectedCount) : L.start)
    : (allTopicsCompleted || completedTopics.includes(unit.unit_name)
         ? L.review : L.start);

  const startHandler = (e) => {
    e.stopPropagation();
    if (!canStart) return;
    goToUnit(unit);
  };

  return (
    <div
      style={{
        background: 'white', borderRadius: 24,
        border: `2px solid ${allTopicsCompleted ? '#6EE7B7' : '#E5E7EB'}`,
        boxShadow: '0 5px 0 #E5E7EB, 0 10px 28px rgba(5,150,105,.07)',
        padding: 20, position: 'relative',
        transition: 'transform .15s, box-shadow .15s, border-color .15s',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Index badge */}
      <span style={{
        position: 'absolute',
        top: 14, [contentDir === 'rtl' ? 'left' : 'right']: 14,
        background: '#D1FAE5', color: '#059669',
        fontSize: 11, fontWeight: 700, borderRadius: 14, padding: '3px 10px',
        fontFamily: "'Sora',sans-serif",
        border: '2px solid rgba(5,150,105,.2)',
        boxShadow: '0 2px 0 rgba(5,150,105,.15)',
      }}>
        {globalIndex + 1}
      </span>

      {/* Unit title */}
      {(() => {
        const lessonTitle = unit.lesson_titles?.[unit.semester] || null;
        return (
          <>
            <div style={{
              fontSize: 15, fontWeight: 700, color: '#1E3A5F',
              marginBottom: lessonTitle ? 4 : 12,
              [contentDir === 'rtl' ? 'paddingLeft' : 'paddingRight']: 50,
              paddingTop: 4, fontFamily: "'Sora',sans-serif", lineHeight: 1.5,
            }}>
              {unit.unit_name}
            </div>

            {/* First lesson title — single line subtitle, keyed by this unit's semester */}
            {lessonTitle && (
              <div style={{
                fontSize: 12, color: '#6B7280', fontWeight: 500,
                marginBottom: 12, lineHeight: 1.4,
                fontFamily: "'Sora',sans-serif",
                [contentDir === 'rtl' ? 'paddingLeft' : 'paddingRight']: 50,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {lessonTitle}
              </div>
            )}
          </>
        );
      })()}

      {/* Topics selection list OR direct-start placeholder */}
      {hasTopics && (
        <div style={{
          borderTop: '1px solid #F3F4F6',
          paddingTop: 10, marginBottom: 10,
        }}>
          {topics.map((tp, i) => {
            const isSelected = selected?.has?.(tp.topic_name) || false;
            const isCompleted = completedTopics.includes(tp.topic_name);
            const displayName = tp.topic_display ?? tp.topic_name;
            return (
              <div
                key={tp.topic_id || i}
                onClick={() => toggleTopic(unit.unit_id, tp.topic_name)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${isSelected ? '#6EE7B7' : 'transparent'}`,
                  background: isSelected ? '#D1FAE5' : 'transparent',
                  marginBottom: 4,
                  transition: 'all .15s',
                }}
                onMouseEnter={e => {
                  if (!isSelected) e.currentTarget.style.background = '#F0FDF4';
                }}
                onMouseLeave={e => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <span style={{
                  width: 18, height: 18,
                  border: `2px solid ${isSelected ? '#059669' : '#9CA3AF'}`,
                  borderRadius: 5, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: isSelected ? '#059669' : 'white',
                  color: 'white', fontSize: 12, fontWeight: 900,
                }}>
                  {isSelected ? '✓' : ''}
                </span>
                <span style={{
                  fontSize: 12.5, color: '#374151',
                  lineHeight: 1.4, flex: 1,
                }}>
                  {displayName}
                </span>
                {isCompleted && (
                  <span style={{ fontSize: 14, flexShrink: 0 }}
                        title={isSelected ? '' : 'completed'}>
                    ✅
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Selection counter (only for units with topics) */}
      {hasTopics && (
        <div style={{
          fontSize: 11,
          color: canStart ? '#059669' : '#9CA3AF',
          fontWeight: canStart ? 700 : 500,
          textAlign: 'center',
          marginBottom: 10, minHeight: 14,
          fontFamily: "'Sora',sans-serif",
        }}>
          {canStart ? L.topics_selected(selectedCount) : L.select_at_least_one}
        </div>
      )}

      {/* Start / Review button */}
      <button
        onClick={startHandler}
        disabled={!canStart}
        style={{
          marginTop: 'auto',
          background: !canStart
            ? '#E5E7EB'
            : (allTopicsCompleted || (!hasTopics && completedTopics.includes(unit.unit_name))
                 ? '#D1FAE5'
                 : 'linear-gradient(135deg,#059669,#10B981)'),
          color: !canStart ? '#9CA3AF'
               : (allTopicsCompleted || (!hasTopics && completedTopics.includes(unit.unit_name))
                    ? '#065F46'
                    : 'white'),
          border: (allTopicsCompleted || (!hasTopics && completedTopics.includes(unit.unit_name)))
                    ? '2px solid #6EE7B7'
                    : 'none',
          borderRadius: 16, padding: '10px 18px', fontSize: 13, fontWeight: 900,
          cursor: canStart ? 'pointer' : 'not-allowed',
          fontFamily: "'Sora',sans-serif", width: '100%',
          boxShadow: !canStart
            ? '0 4px 0 #D1D5DB'
            : (allTopicsCompleted || (!hasTopics && completedTopics.includes(unit.unit_name))
                 ? '0 4px 0 #6EE7B7'
                 : '0 4px 0 #047857, 0 8px 16px rgba(5,150,105,.2)'),
          transition: 'transform .08s, box-shadow .08s',
        }}
      >
        {startLabel}
      </button>

      {unit.has_poem && (
        <button
          onClick={(e) => { e.stopPropagation(); onMemorizationClick?.(unit); }}
          disabled={memLoadingUnit === unit.unit_id}
          style={{
            width: '100%',
            padding: '10px 18px',
            marginTop: 8,
            background: memLoadingUnit === unit.unit_id
              ? '#E5E7EB'
              : 'linear-gradient(135deg,#7C3AED,#A78BFA)',
            color: memLoadingUnit === unit.unit_id ? '#9CA3AF' : '#fff',
            border: 'none',
            borderRadius: 16,
            fontSize: 13, fontWeight: 700,
            cursor: memLoadingUnit === unit.unit_id ? 'wait' : 'pointer',
            fontFamily: "'Sora',sans-serif",
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            boxShadow: memLoadingUnit === unit.unit_id
              ? '0 4px 0 #D1D5DB'
              : '0 4px 0 #5B21B6, 0 8px 16px rgba(124,58,237,.2)',
            transition: 'transform .08s, box-shadow .08s',
          }}
        >
          <span>📖</span>
          <span>{memLoadingUnit === unit.unit_id ? L.hifz_loading : L.hifz_test}</span>
        </button>
      )}
    </div>
  );
}

const CSS = `
  @keyframes sup-spin { to { transform: rotate(360deg) } }
  .sup-spinner {
    width: 36px; height: 36px;
    border: 3px solid #E5E7EB; border-top-color: #059669;
    border-radius: 50%; animation: sup-spin .8s linear infinite;
  }
  .sup-topic-row:hover { background: #F0FDF4; color: #059669; transform: translateY(-1px); border-color: #D1FAE5; box-shadow: 0 2px 8px rgba(5,150,105,.08); }
  .sup-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
`;
